import { Combobox, useCombobox } from "@mantine/core";
import clsx from "clsx";
import React, { useMemo, useState } from "react";

import { Icon } from "../../../../assets/icons/Icon";
import Button from "../../../mantine/button/Button";
import styles from "./SelectFilter.module.scss";

export type VisualState = "checked" | "crossed" | "none";

interface Item {
  value: string;
  label: string;
  count?: number;
  isDivider?: boolean;
  className?: string;
  component?: React.ReactNode;
}

interface SelectFilterProps {
  items: Item[];
  filterTitle: string;
  onSelect: (value: string) => void;
  getVisualState: (value: string) => VisualState;
  setHoveredValue: (value: string | null) => void;
  closeOnSelect?: boolean;
  dropdownAlign?: "left" | "right";
}

const SelectFilter: React.FC<SelectFilterProps> = ({
  items,
  filterTitle,
  onSelect,
  getVisualState,
  setHoveredValue,
  closeOnSelect = true,
  dropdownAlign = "left",
}) => {
  const [isOpened, setIsOpened] = useState(false);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setIsOpened(false);
    },
    onDropdownOpen: () => {
      // Add combobox.focusSearchInput() here if <Combobox.Search> is added.
      // Without it Mantine crashes — searchRef is null when there's no search input.
      setIsOpened(true);
    },
  });

  const options = items.map((item) => {
    if (item.isDivider) {
      return <hr key={`divider-${item.value}`} className={item.className} />;
    }

    const visualState = getVisualState(item.value);
    const isChecked = visualState === "checked";
    const isCrossed = visualState === "crossed";

    return (
      <Combobox.Option value={item.value} key={item.value}>
        <div
          key={item.value}
          className={`${styles.selectFilterOption} ${item.className || ""}`}
          data-dimmed={isCrossed}
          onMouseEnter={() => setHoveredValue(item.value)}
          onMouseLeave={() => setHoveredValue(null)}
        >
          {item.component ? (
            item.component
          ) : (
            <>
              <input
                type="checkbox"
                readOnly
                checked={isChecked}
                data-crossed={isCrossed}
              />
              {/*
               // @ts-expect-error TS2339 non-existing className */}
              <span className={styles.optionLabel}>{item.label}</span>
              {typeof item.count === "number" && (
                // @ts-expect-error TS2339 non-existing className
                <span className={styles.optionCount}>({item.count})</span>
              )}
            </>
          )}
        </div>
      </Combobox.Option>
    );
  });

  const buttonContent = useMemo(() => {
    return (
      <div className={styles.filterButtonContent}>
        <span className={styles.filterTitle}>{filterTitle}</span>
      </div>
    );
  }, [filterTitle]);

  return (
    <div className={styles.selectFilter}>
      <Combobox
        store={combobox}
        withinPortal={false}
        onOptionSubmit={(val) => {
          onSelect(val);
          if (!closeOnSelect) {
            // Keep dropdown open for multi-select
            combobox.openDropdown();
          }
        }}
      >
        <Combobox.Target>
          <Button
            component="button"
            type="button"
            variant="outlined"
            onClick={() => combobox.toggleDropdown()}
            // @ts-expect-error TS2551 non-existing className
            className={styles.selectFilterButton}
            rightSection={<Icon iconName={isOpened ? "IconUp" : "IconDown"} />}
          >
            {buttonContent}
          </Button>
        </Combobox.Target>

        <Combobox.Dropdown
          className={clsx(
            styles.selectFilterDropdown,
            dropdownAlign === "right" && styles.selectFilterDropdownRight,
          )}
        >
          <Combobox.Options>
            {options.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>Nothing Found</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
};

export default SelectFilter;
